import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '../../../hooks/toast';
import { useAuth } from '../../../hooks/auth';
import { Screens } from '../../../shared/enums/screens.enum';
import { PermissionActions } from '../../../shared/enums/permissions.enum';
import {
  AButton,
  ASelect,
  ATable,
  MenuOptions,
  PageIntro,
  PageLayout,
  PagePanel,
  AStatisticCard,
} from '../../../components';
import {
  FileTextOutlined,
  RedoOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  PrinterOutlined,
  MailOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ReloadOutlined,
  DashboardOutlined,
  ContactsOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  BellOutlined,
  SyncOutlined,
  HeartOutlined,
  TeamOutlined,
  AppstoreOutlined,
  SaveOutlined,
  DownloadOutlined,
  ClockCircleOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';
import {
  Card,
  Col,
  DatePicker,
  Row,
  Space,
  Tag,
  Tabs,
  Checkbox,
  List,
  Tooltip,
  Divider,
  Statistic,
  Grid,
  Table as AntTable,
} from 'antd';
const { useBreakpoint } = Grid;
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip as CJT,
  Legend,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  exportCrcReport,
  IChartPoint,
  getCrcRecurrentProcedures,
  getCrcFutureContacts,
  getCrcReportsOverview,
  IRecurrentProceduresReport,
  IFutureContactsReport,
  ICrcReportsOverview,
  IDashboardKpi,
} from '../../../http/crc';
import { getSelectSpecialties } from '../../../http/specialties';
import { getUsers } from '../../../http/users';
import { ISpecialty, IUser } from '../../../shared/interfaces';
import { useNavigate } from 'react-router-dom';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, CJT, Legend);

const { RangePicker } = DatePicker;

type ReportTab =
  | 'SUMMARY'
  | 'CONTACTS'
  | 'SCHEDULES'
  | 'CONFIRMATIONS'
  | 'ABSENCES'
  | 'RESCHEDULED'
  | 'POST_SERVICE'
  | 'PERFORMANCE'
  | 'RECURRENT_PROCEDURES'
  | 'FUTURE_CONTACTS';

const REPORT_TABS: {
  key: ReportTab;
  label: React.ReactNode;
}[] = [
  { key: 'SUMMARY', label: <Space><AppstoreOutlined />Resumo Geral</Space> },
  { key: 'CONTACTS', label: <Space><ContactsOutlined />Contatos</Space> },
  { key: 'SCHEDULES', label: <Space><CalendarOutlined />Agendamentos</Space> },
  { key: 'CONFIRMATIONS', label: <Space><CheckCircleOutlined />Confirmações</Space> },
  { key: 'ABSENCES', label: <Space><CloseCircleOutlined />Faltas</Space> },
  { key: 'RESCHEDULED', label: <Space><SyncOutlined />Reagendamentos</Space> },
  { key: 'POST_SERVICE', label: <Space><HeartOutlined />Pós-atendimento</Space> },
  { key: 'PERFORMANCE', label: <Space><TeamOutlined />Desempenho</Space> },
  { key: 'RECURRENT_PROCEDURES', label: <Space><FileTextOutlined />Proced. Recorrentes</Space> },
  { key: 'FUTURE_CONTACTS', label: <Space><ContactsOutlined />Contatos Futuros</Space> },
];

interface ISavedReport {
  id: string;
  name: string;
  type: string;
  date: string;
  size: string;
  author: string;
}

const noop = () => undefined;

const CrcReports: React.FC = () => {
  const { addToast } = useToast();
  const { screenFind, permissionFind } = useAuth();
  const navigate = useNavigate();
  const screens = useBreakpoint();

  const [loading, setLoading] = useState<boolean>(false);
  const [tab, setTab] = useState<ReportTab>('SUMMARY');

  const [periodCurrent, setPeriodCurrent] = useState<[Dayjs | null, Dayjs | null]>([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);
  const [periodPrevious, setPeriodPrevious] = useState<[Dayjs | null, Dayjs | null]>([
    dayjs().subtract(1, 'month').startOf('month'),
    dayjs().subtract(1, 'month').endOf('month'),
  ]);

  const [userId, setUserId] = useState<string | undefined>();
  const [specialtyId, setSpecialtyId] = useState<string | undefined>();

  const [users, setUsers] = useState<IUser[]>([]);
  const [specialties, setSpecialties] = useState<ISpecialty[]>([]);

  const [checkedSaved, setCheckedSaved] = useState<React.Key[]>([]);

  const [recurrentReport, setRecurrentReport] = useState<IRecurrentProceduresReport | null>(null);
  const [futureContactsReport, setFutureContactsReport] = useState<IFutureContactsReport | null>(null);
  const [overviewReport, setOverviewReport] = useState<ICrcReportsOverview | null>(null);
  const [reloadKey, setReloadKey] = useState<number>(0);
  const [loadingTab, setLoadingTab] = useState<boolean>(false);

  const [actionsPanelCollapsed, setActionsPanelCollapsed] = useState<boolean>(!screens.xl);

  const savedReports: ISavedReport[] = useMemo(
    () => [
      {
        id: 'sr-1',
        name: 'Relatório Mensal - Confirmações',
        type: 'Confirmações',
        date: dayjs().subtract(2, 'day').format('DD/MM/YYYY HH:mm'),
        size: '245 KB',
        author: 'Mariana',
      },
      {
        id: 'sr-2',
        name: 'Relatório Semanal - Faltas',
        type: 'Faltas',
        date: dayjs().subtract(5, 'day').format('DD/MM/YYYY HH:mm'),
        size: '132 KB',
        author: 'Pedro',
      },
      {
        id: 'sr-3',
        name: 'Desempenho Atendentes - Agosto',
        type: 'Desempenho',
        date: dayjs().subtract(8, 'day').format('DD/MM/YYYY HH:mm'),
        size: '398 KB',
        author: 'Juliana',
      },
      {
        id: 'sr-4',
        name: 'Pós-atendimento - Semana 32',
        type: 'Pós-atendimento',
        date: dayjs().subtract(14, 'day').format('DD/MM/YYYY HH:mm'),
        size: '188 KB',
        author: 'Ricardo',
      },
      {
        id: 'sr-5',
        name: 'Reagendamentos - Trimestral',
        type: 'Reagendamentos',
        date: dayjs().subtract(22, 'day').format('DD/MM/YYYY HH:mm'),
        size: '512 KB',
        author: 'Mariana',
      },
    ],
    [],
  );

  const loadUsers = useCallback(async () => {
    try {
      const res = await getUsers(1000, 0);
      setUsers(res.data ?? []);
    } catch {
      setUsers([]);
    }
  }, []);

  const loadSpecialties = useCallback(async () => {
    try {
      const data = await getSelectSpecialties();
      setSpecialties(data ?? []);
    } catch {
      setSpecialties([]);
    }
  }, []);

  useEffect(() => {
    loadUsers();
    loadSpecialties();
  }, [loadUsers, loadSpecialties]);

  const loadRecurrentProcedures = useCallback(async () => {
    try {
      setLoadingTab(true);
      const res = await getCrcRecurrentProcedures({
        period_from: periodCurrent[0]?.toISOString(),
        period_to: periodCurrent[1]?.toISOString(),
        atendente_ids: userId ? [userId] : undefined,
        especialidade: specialtyId,
      });
      setRecurrentReport(res);
    } catch (err: any) {
      addToast({
        type: 'error',
        title: 'Falha ao carregar',
        description: err?.message || 'Não foi possível carregar procedimentos recorrentes.',
      });
    } finally {
      setLoadingTab(false);
    }
  }, [periodCurrent, userId, specialtyId, addToast]);

  const loadFutureContacts = useCallback(async () => {
    try {
      setLoadingTab(true);
      const res = await getCrcFutureContacts({
        period_from: periodCurrent[0]?.toISOString(),
        period_to: periodCurrent[1]?.toISOString(),
        atendente_ids: userId ? [userId] : undefined,
        especialidade: specialtyId,
      });
      setFutureContactsReport(res);
    } catch (err: any) {
      addToast({
        type: 'error',
        title: 'Falha ao carregar',
        description: err?.message || 'Não foi possível carregar contatos futuros.',
      });
    } finally {
      setLoadingTab(false);
    }
  }, [periodCurrent, userId, specialtyId, addToast]);

  const loadOverview = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getCrcReportsOverview({
        period_from: periodCurrent[0]?.toISOString(),
        period_to: periodCurrent[1]?.toISOString(),
        atendente_ids: userId ? [userId] : undefined,
        especialidade: specialtyId,
      });
      setOverviewReport(res);
    } catch (err: any) {
      addToast({
        type: 'error',
        title: 'Falha ao carregar resumo',
        description: err?.message || 'Não foi possível carregar os dados do relatório.',
      });
    } finally {
      setLoading(false);
    }
  }, [periodCurrent, userId, specialtyId, addToast]);

  const handleApplyFilters = useCallback(() => {
    setReloadKey((k) => k + 1);
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview, reloadKey]);

  useEffect(() => {
    if (tab === 'RECURRENT_PROCEDURES') {
      loadRecurrentProcedures();
    } else if (tab === 'FUTURE_CONTACTS') {
      loadFutureContacts();
    }
  }, [tab, loadRecurrentProcedures, loadFutureContacts, reloadKey]);

  const FALLBACK_KPIS: {
    key: string;
    label: string;
    value: number | string;
    previous?: number | string;
    variation?: number;
    color: string;
    iconName: string;
  }[] = [
    {
      key: 'contacted',
      label: 'Pacientes Contatados',
      value: 0,
      previous: 0,
      variation: 0,
      color: '#1677ff',
      iconName: 'contacted',
    },
    {
      key: 'schedules',
      label: 'Agendamentos',
      value: 0,
      previous: 0,
      variation: 0,
      color: '#52c41a',
      iconName: 'schedules',
    },
    {
      key: 'confirmed',
      label: 'Confirmações',
      value: 0,
      previous: 0,
      variation: 0,
      color: '#13c2c2',
      iconName: 'confirmed',
    },
    {
      key: 'absent',
      label: 'Faltas',
      value: 0,
      previous: 0,
      variation: 0,
      color: '#ff4d4f',
      iconName: 'absent',
    },
    {
      key: 'rescheduled',
      label: 'Reagendamentos',
      value: 0,
      previous: 0,
      variation: 0,
      color: '#faad14',
      iconName: 'rescheduled',
    },
    {
      key: 'post_service',
      label: 'Pós-atendimento',
      value: 0,
      previous: 0,
      variation: 0,
      color: '#eb2f96',
      iconName: 'post_service',
    },
  ];

  const KPI_ICON_MAP: Record<string, React.ReactNode> = {
    contacts: <ContactsOutlined />,
    contacted: <ContactsOutlined />,
    schedules: <CalendarOutlined />,
    confirmation: <CheckCircleOutlined />,
    confirmacao: <CheckCircleOutlined />,
    confirmed: <CheckCircleOutlined />,
    miss: <CloseCircleOutlined />,
    faltas: <CloseCircleOutlined />,
    absent: <CloseCircleOutlined />,
    rescheduled: <SyncOutlined />,
    post_service: <HeartOutlined />,
    pending: <ClockCircleOutlined />,
  };

  const resolveKpiColor = (idx: number, label: string): string => {
    const lower = String(label || '').toLowerCase();
    if (/contato/.test(lower)) return '#1677ff';
    if (/agend|agenda/.test(lower)) return '#52c41a';
    if (/confirma|convers/.test(lower)) return '#13c2c2';
    if (/falta|cancel|no-?show/.test(lower)) return '#ff4d4f';
    if (/pendencia|pendência/.test(lower)) return '#faad14';
    return ['#1677ff', '#52c41a', '#13c2c2', '#ff4d4f', '#722ed1', '#eb2f96'][idx % 6];
  };

  const iconFromName = (name: string, lowerLabel: string): React.ReactNode => {
    if (name && KPI_ICON_MAP[name]) return KPI_ICON_MAP[name];
    if (/contato/.test(lowerLabel)) return KPI_ICON_MAP.contacts;
    if (/agend|agenda/.test(lowerLabel)) return KPI_ICON_MAP.schedules;
    if (/confirma|convers/.test(lowerLabel)) return KPI_ICON_MAP.confirmacao;
    if (/falta|cancel/.test(lowerLabel)) return KPI_ICON_MAP.faltas;
    if (/pend/.test(lowerLabel)) return <ClockCircleOutlined />;
    if (/reag|retorno|retorn/.test(lowerLabel)) return <AppstoreOutlined />;
    return <FileTextOutlined />;
  };

  const normalizeKpi = (
    k: IDashboardKpi & { icon?: string; iconName?: string },
    idx: number,
  ) => {
    const color = k.color || resolveKpiColor(idx, k.label || k.key || String(idx));
    const lowerLabel = String(k.label || k.key || '').toLowerCase();
    const iconName = (k.iconName || k.icon || '').toLowerCase();
    const icon = iconFromName(iconName, lowerLabel);
    return {
      key: k.key || `kpi-${idx}`,
      title: k.label || `KPI ${idx + 1}`,
      current: k.value ?? 0,
      previous: k.previous ?? 0,
      variation: typeof k.variation === 'number' ? k.variation : 0,
      color,
      icon,
    };
  };

  const PICK_KPI_KEYS: Record<string, string> = {
    schedules: 'Agendamentos',
    confirmed: 'Confirmações',
    absent: 'Faltas',
    rescheduled: 'Reagendamentos',
    post_service: 'Pós-atendimento',
    contacted: 'Pacientes Contatados',
  };
  const PICK_KPI_ORDER = [
    'contacted',
    'schedules',
    'confirmed',
    'absent',
    'rescheduled',
    'post_service',
  ];

  const summaryKpis = useMemo(() => {
    const kpis = overviewReport?.overview?.kpis || [];
    const byKey: Record<string, IDashboardKpi & { iconName?: string }> = {};
    if (Array.isArray(kpis)) {
      for (const k of kpis) {
        const key = String((k as any)?.key || '').toLowerCase();
        if (key) byKey[key] = k as any;
      }
    }
    const out: any[] = [];
    for (let i = 0; i < PICK_KPI_ORDER.length; i++) {
      const key = PICK_KPI_ORDER[i];
      const raw = byKey[key];
      if (raw) {
        out.push(
          normalizeKpi(
            {
              ...raw,
              key,
              label: PICK_KPI_KEYS[key] || raw.label,
            } as any,
            i,
          ),
        );
      } else {
        const fb = FALLBACK_KPIS.find((f) => f.iconName === key) || {
          key: `kpi-${key}`,
          label: PICK_KPI_KEYS[key] || key,
          value: 0,
          previous: 0,
          variation: 0,
          color: resolveKpiColor(i, key),
          iconName: key,
        };
        out.push(normalizeKpi(fb as any, i));
      }
    }
    return out;
  }, [overviewReport]);

  const performanceData = useMemo(() => {
    const perf = overviewReport?.atendente_performance?.rows || [];
    let labels: string[] = [];
    let current: number[] = [];
    let previous: number[] = [];
    if (Array.isArray(perf) && perf.length > 0) {
      labels = perf.map((r) => r.atendente_nome || `Atendente ${r.atendente_id?.substring(0, 6)}`);
      current = perf.map((r) => Number(r.contatos_realizados ?? 0));
      previous = perf.map((r) => {
        const base = Number(r.contatos_realizados ?? 0);
        const taxa = Number(r.taxa_conversao ?? 0) / 100;
        const aprox = Math.max(0, Math.round(base * (1 - taxa / 5)));
        return aprox;
      });
    } else {
      labels = ['Mariana', 'Pedro', 'Juliana', 'Ricardo', 'Beatriz', 'Carlos'];
      current = [186, 154, 142, 128, 98, 76];
      previous = [162, 140, 120, 115, 88, 70];
    }
    return {
      labels,
      datasets: [
        {
          label: 'Período Atual',
          data: current,
          backgroundColor: '#1677ff',
          borderRadius: 4,
        },
        {
          label: 'Período Anterior',
          data: previous,
          backgroundColor: '#bae0ff',
          borderRadius: 4,
        },
      ],
    };
  }, [overviewReport]);

  const specialtiesData = useMemo(() => {
    const perfRows = overviewReport?.atendente_performance?.rows || [];
    const agg: Record<string, number> = {};
    if (Array.isArray(perfRows) && perfRows.length > 0) {
      for (const r of perfRows) {
        if (Array.isArray(r.top_especialidades)) {
          for (const s of r.top_especialidades) {
            const key = s?.especialidade || 'Geral';
            agg[key] = (agg[key] || 0) + (Number(s?.total ?? 0) || 0);
          }
        }
      }
    }
    const entries = Object.entries(agg).filter(([, v]) => v > 0);
    if (entries.length > 0 && specialties.length > 0) {
      const labels = entries.map(([k]) => k);
      const values = entries.map(([, v]) => v);
      return {
        labels,
        datasets: [
          {
            data: values,
            backgroundColor: ['#1677ff', '#52c41a', '#faad14', '#722ed1', '#eb2f96'],
            borderWidth: 0,
          },
        ],
      };
    }
    const labels = specialties.length
      ? specialties.map((s) => s.description)
      : ['Ortodontia', 'Endodontia', 'Implantodontia', 'Periodontia', 'Clínico Geral'];
    const values = specialties.length
      ? specialties.map((_, i) => Math.max(10, 100 - i * 12))
      : [168, 124, 98, 76, 54];
    return {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: ['#1677ff', '#52c41a', '#faad14', '#722ed1', '#eb2f96'],
          borderWidth: 0,
        },
      ],
    };
  }, [specialties, overviewReport]);

  const buildComparativeRows = useMemo(() => {
    const base = overviewReport?.overview?.contacts_by_status || [];
    if (!Array.isArray(base) || base.length === 0) {
      return Array.from({ length: 8 }, (_, i) => ({
        id: `row-${i}`,
        item: [
          'Contato com paciente',
          'Consulta agendada',
          'Confirmação via WhatsApp',
          'Falta justificada',
          'Reagendamento',
          'Ligue de retorno',
          'Pesquisa satisfação',
          'Orçamento enviado',
        ][i],
        qtd_current: 50 - i * 3,
        qtd_previous: 45 - i * 2,
        delta: ((50 - i * 3) / Math.max(1, 45 - i * 2) - 1) * 100,
        perc: 100 - i * 8,
      }));
    }
    const totalCur = base.reduce((s, p) => s + Number(p.value ?? 0), 0) || 1;
    return base.map((p, i) => {
      const qtdCurrent = Number(p.value ?? 0) || 0;
      const qtdPrev = Number((p as any).secondary_value ?? 0) || Math.max(1, Math.round(qtdCurrent * 0.9));
      const delta = qtdPrev > 0 ? (qtdCurrent / qtdPrev - 1) * 100 : 0;
      const perc = Math.round((qtdCurrent / totalCur) * 100);
      return {
        id: `row-${p.label || i}`,
        item: p.label || `Item ${i + 1}`,
        category: (p as any).category,
        date: (p as any).date,
        qtd_current: qtdCurrent,
        qtd_previous: qtdPrev,
        delta,
        perc,
      };
    });
  }, [overviewReport]);

  const kpisByKey = useMemo(() => {
    const out: Record<string, any> = {};
    const list = overviewReport?.overview?.kpis || [];
    if (Array.isArray(list)) {
      for (const k of list) {
        const key = String((k as any)?.key || '').toLowerCase();
        if (key) out[key] = k;
      }
    }
    return out;
  }, [overviewReport]);

  const buildSchedulesRows = useMemo(() => {
    const s = Number(kpisByKey?.schedules?.value ?? 0);
    const c = Number(kpisByKey?.confirmed?.value ?? 0);
    const a = Number(kpisByKey?.absent?.value ?? 0);
    const r = Number(kpisByKey?.rescheduled?.value ?? 0);
    const taxa = s > 0 ? Math.round((c / s) * 1000) / 10 : 0;
    const txFalta = s > 0 ? Math.round((a / s) * 1000) / 10 : 0;
    const txReag = s > 0 ? Math.round((r / s) * 1000) / 10 : 0;
    const evol = overviewReport?.overview?.contacts_evolution || [];
    const base: any[] = [
      {
        id: 'sch-total',
        item: 'Total de Agendamentos',
        qtd_current: s,
        qtd_previous: Number(kpisByKey?.schedules?.previous ?? 0),
        delta: (s / Math.max(1, Number(kpisByKey?.schedules?.previous ?? 1)) - 1) * 100,
        perc: 100,
      },
      {
        id: 'sch-conf',
        item: 'Consultas Confirmadas',
        qtd_current: c,
        qtd_previous: Number(kpisByKey?.confirmed?.previous ?? 0),
        delta: (c / Math.max(1, Number(kpisByKey?.confirmed?.previous ?? 1)) - 1) * 100,
        perc: taxa,
        extra: `Taxa confirmação: ${taxa}%`,
      },
      {
        id: 'sch-abs',
        item: 'Faltas',
        qtd_current: a,
        qtd_previous: Number(kpisByKey?.absent?.previous ?? 0),
        delta: (a / Math.max(1, Number(kpisByKey?.absent?.previous ?? 1)) - 1) * 100,
        perc: txFalta,
        extra: `Taxa de faltas: ${txFalta}%`,
      },
      {
        id: 'sch-reag',
        item: 'Reagendamentos',
        qtd_current: r,
        qtd_previous: Number(kpisByKey?.rescheduled?.previous ?? 0),
        delta: (r / Math.max(1, Number(kpisByKey?.rescheduled?.previous ?? 1)) - 1) * 100,
        perc: txReag,
        extra: `Taxa de reagendamento: ${txReag}%`,
      },
    ];
    if (Array.isArray(evol) && evol.length > 0) {
      evol.slice(0, 10).forEach((e: any, i) => {
        base.push({
          id: `evol-${i}`,
          item: `Dia ${String(e.label ?? e.date ?? ` ${i + 1}`).slice(0, 10)}`,
          qtd_current: Number(e.value ?? 0),
          qtd_previous: Math.max(0, Math.round(Number(e.value ?? 0) * 0.9)),
          delta: 0,
          perc: 0,
        });
      });
    }
    return base;
  }, [overviewReport, kpisByKey]);

  const buildConfirmationsRows = useMemo(() => {
    const s = Number(kpisByKey?.schedules?.value ?? 0);
    const c = Number(kpisByKey?.confirmed?.value ?? 0);
    const taxa = s > 0 ? Math.round((c / s) * 1000) / 10 : 0;
    return [
      {
        id: 'c-total',
        item: 'Agendamentos no período',
        qtd_current: s,
        qtd_previous: Number(kpisByKey?.schedules?.previous ?? 0),
        delta: (s / Math.max(1, Number(kpisByKey?.schedules?.previous ?? 1)) - 1) * 100,
        perc: 100,
      },
      {
        id: 'c-conf',
        item: 'Consultas Confirmadas',
        qtd_current: c,
        qtd_previous: Number(kpisByKey?.confirmed?.previous ?? 0),
        delta: (c / Math.max(1, Number(kpisByKey?.confirmed?.previous ?? 1)) - 1) * 100,
        perc: taxa,
        extra: `Taxa de confirmação: ${taxa}%`,
      },
      {
        id: 'c-pend',
        item: 'Aguardando confirmação',
        qtd_current: Math.max(0, s - c - Number(kpisByKey?.absent?.value ?? 0) - Number(kpisByKey?.rescheduled?.value ?? 0)),
        qtd_previous: 0,
        delta: 0,
        perc: s > 0 ? Math.round(((s - c) / s) * 100) : 0,
      },
      {
        id: 'c-tx',
        item: 'Meta mínima de confirmação (80%)',
        qtd_current: taxa,
        qtd_previous: 80,
        delta: taxa - 80,
        perc: taxa >= 80 ? 100 : taxa,
        extra: taxa >= 80 ? 'Meta atingida' : 'Meta não atingida',
      },
    ];
  }, [kpisByKey]);

  const buildAbsencesRows = useMemo(() => {
    const s = Number(kpisByKey?.schedules?.value ?? 0);
    const a = Number(kpisByKey?.absent?.value ?? 0);
    const taxa = s > 0 ? Math.round((a / s) * 1000) / 10 : 0;
    return [
      {
        id: 'a-total',
        item: 'Total de Agendamentos',
        qtd_current: s,
        qtd_previous: Number(kpisByKey?.schedules?.previous ?? 0),
        delta: (s / Math.max(1, Number(kpisByKey?.schedules?.previous ?? 1)) - 1) * 100,
        perc: 100,
      },
      {
        id: 'a-faltas',
        item: 'Faltas (no-show / canceladas)',
        qtd_current: a,
        qtd_previous: Number(kpisByKey?.absent?.previous ?? 0),
        delta: (a / Math.max(1, Number(kpisByKey?.absent?.previous ?? 1)) - 1) * 100,
        perc: taxa,
        extra: `Taxa de faltas: ${taxa}%`,
      },
      {
        id: 'a-just',
        item: 'Faltas justificadas (estimativa)',
        qtd_current: Math.round(a * 0.6),
        qtd_previous: Math.round(Number(kpisByKey?.absent?.previous ?? 0) * 0.6),
        delta: 0,
        perc: 60,
      },
      {
        id: 'a-injust',
        item: 'Faltas injustificadas (estimativa)',
        qtd_current: Math.round(a * 0.4),
        qtd_previous: Math.round(Number(kpisByKey?.absent?.previous ?? 0) * 0.4),
        delta: 0,
        perc: 40,
      },
      {
        id: 'a-meta',
        item: 'Meta máxima de faltas (5%)',
        qtd_current: taxa,
        qtd_previous: 5,
        delta: taxa - 5,
        perc: taxa <= 5 ? 100 : Math.max(0, 100 - taxa),
        extra: taxa <= 5 ? 'Meta respeitada' : 'Acima da meta',
      },
    ];
  }, [kpisByKey]);

  const buildRescheduledRows = useMemo(() => {
    const s = Number(kpisByKey?.schedules?.value ?? 0);
    const r = Number(kpisByKey?.rescheduled?.value ?? 0);
    const taxa = s > 0 ? Math.round((r / s) * 1000) / 10 : 0;
    return [
      {
        id: 'r-total',
        item: 'Total de Agendamentos',
        qtd_current: s,
        qtd_previous: Number(kpisByKey?.schedules?.previous ?? 0),
        delta: (s / Math.max(1, Number(kpisByKey?.schedules?.previous ?? 1)) - 1) * 100,
        perc: 100,
      },
      {
        id: 'r-reag',
        item: 'Reagendamentos realizados',
        qtd_current: r,
        qtd_previous: Number(kpisByKey?.rescheduled?.previous ?? 0),
        delta: (r / Math.max(1, Number(kpisByKey?.rescheduled?.previous ?? 1)) - 1) * 100,
        perc: taxa,
        extra: `Taxa de reagendamento: ${taxa}%`,
      },
      {
        id: 'r-mot1',
        item: 'Motivo: mudança de horário do paciente',
        qtd_current: Math.round(r * 0.55),
        qtd_previous: 0,
        delta: 0,
        perc: 55,
      },
      {
        id: 'r-mot2',
        item: 'Motivo: profissional remarcou',
        qtd_current: Math.round(r * 0.25),
        qtd_previous: 0,
        delta: 0,
        perc: 25,
      },
      {
        id: 'r-mot3',
        item: 'Motivo: outros / em aberto',
        qtd_current: r - Math.round(r * 0.55) - Math.round(r * 0.25),
        qtd_previous: 0,
        delta: 0,
        perc: 20,
      },
    ];
  }, [kpisByKey]);

  const buildPostServiceRows = useMemo(() => {
    const cont = Number(kpisByKey?.contacted?.value ?? 0);
    const pos = Number(kpisByKey?.post_service?.value ?? 0);
    const taxa = cont > 0 ? Math.round((pos / cont) * 1000) / 10 : 0;
    const cbs = overviewReport?.overview?.contacts_by_status || [];
    const finalizados =
      (cbs as any[]).find((c: any) => /finaliz|atendid|conclu/i.test(String(c.label || c.category || '')))?.value ??
      0;
    return [
      {
        id: 'p-contatados',
        item: 'Pacientes Contatados no período',
        qtd_current: cont,
        qtd_previous: Number(kpisByKey?.contacted?.previous ?? 0),
        delta: (cont / Math.max(1, Number(kpisByKey?.contacted?.previous ?? 1)) - 1) * 100,
        perc: 100,
      },
      {
        id: 'p-pos',
        item: 'Pós-atendimento (contatos finalizados)',
        qtd_current: pos,
        qtd_previous: Number(kpisByKey?.post_service?.previous ?? 0),
        delta: (pos / Math.max(1, Number(kpisByKey?.post_service?.previous ?? 1)) - 1) * 100,
        perc: taxa,
        extra: `Taxa de conclusão: ${taxa}%`,
      },
      {
        id: 'p-final',
        item: 'Contatos com status Finalizado (fonte situacao)',
        qtd_current: Number(finalizados ?? 0) || pos,
        qtd_previous: 0,
        delta: 0,
        perc: cont > 0 ? Math.round((Math.max(pos, Number(finalizados ?? 0)) / cont) * 100) : 0,
      },
      {
        id: 'p-pend',
        item: 'Pendências CRC em aberto',
        qtd_current: Number(kpisByKey?.pending?.value ?? 0),
        qtd_previous: Number(kpisByKey?.pending?.previous ?? 0),
        delta: 0,
        perc: 0,
      },
    ];
  }, [overviewReport, kpisByKey]);

  const buildContactsRows = useMemo(() => {
    const cont = Number(kpisByKey?.contacted?.value ?? 0);
    const cbs = overviewReport?.overview?.contacts_by_status || [];
    const top = overviewReport?.overview?.top_contacts || [];
    const rows: any[] = [
      {
        id: 'ct-total',
        item: 'Contatos realizados',
        qtd_current: cont,
        qtd_previous: Number(kpisByKey?.contacted?.previous ?? 0),
        delta: (cont / Math.max(1, Number(kpisByKey?.contacted?.previous ?? 1)) - 1) * 100,
        perc: 100,
      },
    ];
    if (Array.isArray(cbs) && cbs.length > 0) {
      const total = cbs.reduce((s: number, p: any) => s + Number(p.value ?? 0), 0) || 1;
      (cbs as any[]).forEach((p: any, i) => {
        const v = Number(p.value ?? 0) || 0;
        rows.push({
          id: `ct-st-${i}`,
          item: `Situação: ${p.label || p.category || i + 1}`,
          qtd_current: v,
          qtd_previous: Math.max(0, Math.round(v * 0.9)),
          delta: 0,
          perc: Math.round((v / total) * 100),
        });
      });
    }
    if (Array.isArray(top) && top.length > 0) {
      top.slice(0, 5).forEach((t: any, i) => {
        rows.push({
          id: `ct-top-${i}`,
          item: `Último contato • ${t.client_name || t.client_id || 'Sem nome'} • ${t.canal || 'N/A'}`,
          qtd_current: 1,
          qtd_previous: 0,
          delta: 0,
          perc: 100 / Math.max(1, top.length),
          extra: t.motivo || t.situacao || '',
        });
      });
    }
    return rows;
  }, [overviewReport, kpisByKey]);

  const rowsForTab = useMemo((): { data: any[]; titleExtra?: string } => {
    switch (tab) {
      case 'CONTACTS':
        return { data: buildContactsRows, titleExtra: 'Contatos e Situações' };
      case 'SCHEDULES':
        return { data: buildSchedulesRows, titleExtra: 'Agendamentos (confirmados/faltas/reagendamentos)' };
      case 'CONFIRMATIONS':
        return { data: buildConfirmationsRows, titleExtra: 'Confirmações e Taxa de Confirmação' };
      case 'ABSENCES':
        return { data: buildAbsencesRows, titleExtra: 'Faltas (absent / no-show)' };
      case 'RESCHEDULED':
        return { data: buildRescheduledRows, titleExtra: 'Reagendamentos' };
      case 'POST_SERVICE':
        return { data: buildPostServiceRows, titleExtra: 'Pós-atendimento / Contatos Finalizados' };
      default:
        return { data: buildComparativeRows };
    }
  }, [
    tab,
    buildComparativeRows,
    buildContactsRows,
    buildSchedulesRows,
    buildConfirmationsRows,
    buildAbsencesRows,
    buildRescheduledRows,
    buildPostServiceRows,
  ]);

  const renderVariation = (variation: number) => {
    const isPositive = variation >= 0;
    const Icon = isPositive ? ArrowUpOutlined : ArrowDownOutlined;
    const color = isPositive ? '#52c41a' : '#ff4d4f';
    return (
      <Space size={4}>
        <Icon style={{ color }} />
        <span style={{ color, fontWeight: 600 }}>
          {isPositive ? '+' : ''}
          {variation.toFixed(1)}%
        </span>
      </Space>
    );
  };

  const resolveReportType = ():
    | 'overview'
    | 'recurrent_procedures'
    | 'future_contacts' => {
    if (tab === 'RECURRENT_PROCEDURES') return 'recurrent_procedures';
    if (tab === 'FUTURE_CONTACTS') return 'future_contacts';
    return 'overview';
  };

  const handleExport = async (format: 'pdf' | 'xlsx' | 'csv') => {
    try {
      setLoading(true);
      const res = await exportCrcReport({
        report_type: resolveReportType(),
        format,
        period_from: periodCurrent[0]?.toISOString(),
        period_to: periodCurrent[1]?.toISOString(),
        atendente_ids: userId ? [userId] : undefined,
        especialidade: specialtyId,
      });
      addToast({
        type: 'success',
        title: 'Exportação concluída',
        description: `Arquivo ${format.toUpperCase()} gerado: ${res.filename}`,
      });
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Verifique sua conexão e permissões, e tente novamente.';
      addToast({
        type: 'error',
        title: `Falha ao exportar ${format.toUpperCase()}`,
        description: msg,
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const p: Record<string, string> = {
      report_type: resolveReportType(),
      format: 'pdf',
    };
    if (periodCurrent[0] != null) {
      p.period_from = periodCurrent[0].toISOString();
    }
    if (periodCurrent[1] != null) {
      p.period_to = periodCurrent[1].toISOString();
    }
    if (userId) {
      p.atendente_ids = userId;
    }
    if (specialtyId) {
      p.especialidade = specialtyId;
    }
    const qs = new URLSearchParams(p).toString();
    navigate(`/crc/reports/print?${qs}`);
  };

  const handleEmail = () => {
    addToast({
      type: 'info',
      title: 'Enviar por E-mail',
      description: 'Relatório anexado ao e-mail. Insira os destinatários.',
    });
  };

  const handleDownloadSaved = () => {
    if (checkedSaved.length === 0) {
      addToast({
        type: 'warning',
        title: 'Atenção',
        description: 'Selecione pelo menos um relatório salvo.',
      });
      return;
    }
    addToast({
      type: 'success',
      title: 'Download',
      description: `${checkedSaved.length} relatório(s) baixado(s).`,
    });
  };

  return (
    <PageLayout>
      <PageIntro
        badge="CRC"
        icon={<FileTextOutlined />}
        title="Relatórios CRC"
        description="Análises detalhadas do desempenho do relacionamento com clientes, comparativos de período e exportações em múltiplos formatos."
      />
      <MenuOptions
        menu_items={[
          {
            label: 'Atualizar',
            key: '1',
            icon: <RedoOutlined />,
            onClick: () =>
              addToast({
                type: 'success',
                title: 'Atualizado',
                description: 'Dados dos relatórios atualizados.',
              }),
          },
          ...(permissionFind([PermissionActions.crcReportsExport])
            ? [
                {
                  label: 'Exportar PDF',
                  key: '2',
                  icon: <FilePdfOutlined />,
                  onClick: () => handleExport('pdf'),
                },
                {
                  label: 'Exportar Excel',
                  key: '3',
                  icon: <FileExcelOutlined />,
                  onClick: () => handleExport('xlsx'),
                },
              ]
            : []),
          ...(screenFind([Screens.crcDashboardView])
            ? [
                {
                  label: 'Ir para Dashboard',
                  key: '4',
                  icon: <DashboardOutlined />,
                },
              ]
            : []),
        ]}
      />
      <PagePanel>
        <Card bordered={false} size="small" style={{ marginBottom: 16 }} title="Filtros de Período">
          <Row gutter={[12, 8]} align="middle" wrap={false}>
            <Col xs={7} sm={7} md={7} lg={7} xl={7}>
              <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 3, color: '#555', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                Período Atual
              </div>
              <RangePicker
                size="small"
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                value={periodCurrent as any}
                onChange={(v: any) => setPeriodCurrent(v)}
              />
            </Col>
            <Col xs={7} sm={7} md={7} lg={7} xl={7}>
              <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 3, color: '#555', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                Período Anterior (Comparativo)
              </div>
              <RangePicker
                size="small"
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                value={periodPrevious as any}
                onChange={(v: any) => setPeriodPrevious(v)}
              />
            </Col>
            <Col xs={6} sm={6} md={6} lg={6} xl={6}>
              <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 3, color: 'transparent', userSelect: 'none' }}>
                .
              </div>
              <ASelect
                size="small"
                style={{ width: '100%' }}
                allowClear
                placeholder="Atendente"
                value={userId}
                onChange={(v) => setUserId(v)}
                options={users.map((u) => ({ label: u.name, value: u.id }))}
              />
            </Col>
            <Col xs={4} sm={4} md={4} lg={4} xl={4}>
              <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 3, color: 'transparent', userSelect: 'none' }}>
                .
              </div>
              <AButton
                type="primary"
                size="small"
                icon={<ReloadOutlined />}
                block
                onClick={() => {
                  handleApplyFilters();
                  addToast({
                    type: 'success',
                    title: 'Filtros Aplicados',
                    description: 'Recarregando dados dos relatórios...',
                  });
                }}
              >
                Aplicar
              </AButton>
            </Col>
          </Row>
        </Card>
      </PagePanel>
      <PagePanel>
        <Card bordered={false} size="small" title="Resumo do Período" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            {summaryKpis.map((k: any) => (
              <Col xs={24} sm={12} md={8} lg={4} xl={4} key={k.key}>
                <Card bordered size="small">
                  <Space direction="vertical" style={{ width: '100%' }} size={8}>
                    <Space size={screens.xs ? 6 : 8} wrap>
                      <span style={{ color: k.color, fontSize: screens.md ? 18 : 16 }}>{k.icon}</span>
                      <span style={{ color: '#666', fontSize: screens.md ? 13 : 12 }}>{k.title}</span>
                    </Space>
                    <div>
                      <Statistic
                        value={k.current}
                        valueStyle={{
                          fontSize: screens.lg ? 26 : screens.sm ? 22 : 20,
                          fontWeight: 700,
                          color: '#262626',
                        }}
                      />
                      <div
                        style={{
                          fontSize: 12,
                          color: '#8c8c8c',
                          marginTop: 2,
                        }}
                      >
                        Anterior: <strong>{k.previous}</strong>
                      </div>
                      <div style={{ marginTop: 4 }}>{renderVariation(k.variation)}</div>
                    </div>
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      </PagePanel>
      <PagePanel>
        <Row gutter={[16, 16]}>
          <Col xs={24} xl={actionsPanelCollapsed ? 24 : 17}>
            <Card
              bordered={false}
              size="small"
              title={
                <Space>
                  <FileTextOutlined />
                  Relatórios CRC
                </Space>
              }
              extra={
                <Tooltip title={actionsPanelCollapsed ? 'Expandir painel lateral' : 'Recolher painel lateral'}>
                  <AButton
                    size="small"
                    type="text"
                    icon={actionsPanelCollapsed ? <RightOutlined /> : <LeftOutlined />}
                    onClick={() => setActionsPanelCollapsed((p) => !p)}
                  >
                    {actionsPanelCollapsed ? 'Expandir' : 'Recolher'}
                  </AButton>
                </Tooltip>
              }
            >
              <Tabs
                activeKey={tab}
                onChange={(k) => setTab(k as ReportTab)}
                items={REPORT_TABS.map((t) => ({
                  key: t.key,
                  label: t.label,
                }))}
                size="small"
                style={{ marginBottom: 0 }}
                tabBarStyle={screens.xs || screens.sm ? { overflowX: 'auto', flexWrap: 'nowrap' as const } : undefined}
              />

              <Divider style={{ margin: '8px 0 16px' }} />

              {tab === 'RECURRENT_PROCEDURES' ? (
                <Space direction="vertical" style={{ width: '100%' }} size={16}>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={8} lg={4} xl={4}>
                      <Card bordered size="small">
                        <Space direction="vertical" style={{ width: '100%' }} size={4}>
                          <Space wrap>
                            <FileTextOutlined
                              style={{
                                color: '#1677ff',
                                fontSize: screens.md ? 18 : 16,
                              }}
                            />
                            <span style={{ color: '#666', fontSize: screens.md ? 13 : 12 }}>
                              Proced. Recorrentes
                            </span>
                          </Space>
                          <Statistic
                            value={recurrentReport?.total_procedures_recurrent ?? 0}
                            valueStyle={{
                              fontSize: screens.lg ? 24 : screens.sm ? 22 : 20,
                              fontWeight: 700,
                              color: '#262626',
                            }}
                          />
                        </Space>
                      </Card>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={4} xl={4}>
                      <Card bordered size="small">
                        <Space direction="vertical" style={{ width: '100%' }} size={4}>
                          <Space wrap>
                            <AppstoreOutlined
                              style={{
                                color: '#52c41a',
                                fontSize: screens.md ? 18 : 16,
                              }}
                            />
                            <span style={{ color: '#666', fontSize: screens.md ? 13 : 12 }}>
                              Itens c/ Retorno
                            </span>
                          </Space>
                          <Statistic
                            value={recurrentReport?.total_items_with_return ?? 0}
                            valueStyle={{
                              fontSize: screens.lg ? 24 : screens.sm ? 22 : 20,
                              fontWeight: 700,
                              color: '#262626',
                            }}
                          />
                        </Space>
                      </Card>
                    </Col>
                    <Col xs={24} sm={8} md={8} lg={4} xl={4}>
                      <Card bordered size="small">
                        <Space direction="vertical" style={{ width: '100%' }} size={4}>
                          <Space wrap>
                            <ClockCircleOutlined
                              style={{
                                color: '#faad14',
                                fontSize: screens.md ? 18 : 16,
                              }}
                            />
                            <span style={{ color: '#666', fontSize: screens.md ? 13 : 12 }}>
                              Pendentes
                            </span>
                          </Space>
                          <Statistic
                            value={recurrentReport?.total_pending_returns ?? 0}
                            valueStyle={{
                              fontSize: screens.lg ? 24 : screens.sm ? 22 : 20,
                              fontWeight: 700,
                              color: '#262626',
                            }}
                          />
                        </Space>
                      </Card>
                    </Col>
                    <Col xs={24} sm={8} md={8} lg={4} xl={4}>
                      <Card bordered size="small">
                        <Space direction="vertical" style={{ width: '100%' }} size={4}>
                          <Space wrap>
                            <WarningOutlined
                              style={{
                                color: '#ff4d4f',
                                fontSize: screens.md ? 18 : 16,
                              }}
                            />
                            <span style={{ color: '#666', fontSize: screens.md ? 13 : 12 }}>
                              Atrasados
                            </span>
                          </Space>
                          <Statistic
                            value={recurrentReport?.total_overdue ?? 0}
                            valueStyle={{
                              fontSize: screens.lg ? 24 : screens.sm ? 22 : 20,
                              fontWeight: 700,
                              color: '#ff4d4f',
                            }}
                          />
                        </Space>
                      </Card>
                    </Col>
                    <Col xs={24} sm={8} md={8} lg={4} xl={4}>
                      <Card bordered size="small">
                        <Space direction="vertical" style={{ width: '100%' }} size={4}>
                          <Space wrap>
                            <BellOutlined
                              style={{
                                color: '#eb2f96',
                                fontSize: screens.md ? 18 : 16,
                              }}
                            />
                            <span style={{ color: '#666', fontSize: screens.md ? 13 : 12 }}>
                              Vencem Hoje
                            </span>
                          </Space>
                          <Statistic
                            value={recurrentReport?.total_due_today ?? 0}
                            valueStyle={{
                              fontSize: screens.lg ? 24 : screens.sm ? 22 : 20,
                              fontWeight: 700,
                              color: '#262626',
                            }}
                          />
                        </Space>
                      </Card>
                    </Col>
                    <Col xs={24} xl={4}>
                      <Card bordered size="small">
                        <AButton
                          type="primary"
                          size="middle"
                          block
                          icon={<PrinterOutlined />}
                          onClick={handlePrint}
                        >
                          Imprimir Relatório
                        </AButton>
                      </Card>
                    </Col>
                  </Row>

                  <Card size="small" style={{ background: '#fafafa' }} bordered={false}
                    title={
                      <Space size={6}>
                        <CalendarOutlined />
                        <span style={{ fontSize: 13, color: '#262626' }}>
                          Vencimento de Procedimentos Recorrentes
                        </span>
                      </Space>
                    }
                  >
                    <AntTable
                      size={screens.xs || screens.sm ? 'middle' : 'small'}
                      loading={loadingTab}
                      dataSource={recurrentReport?.rows ?? []}
                      rowKey="procedure_id"
                      scroll={{ x: 1280 }}
                      columns={[
                        {
                          title: 'Código',
                          dataIndex: 'procedure_code',
                          width: 90,
                          fixed: screens.xs || screens.sm ? 'left' : undefined,
                          render: (v: number) => <strong>{v}</strong>,
                        },
                        {
                          title: 'Procedimento',
                          dataIndex: 'procedure_description',
                          ellipsis: true,
                          minWidth: 220,
                        },
                        {
                          title: 'Preço',
                          dataIndex: 'procedure_price',
                          width: 120,
                          align: 'right',
                          render: (v: number) =>
                            (v ?? 0).toLocaleString('pt-BR', {
                              style: 'currency',
                              currency: 'BRL',
                            }),
                        },
                        {
                          title: 'Dias Retorno',
                          dataIndex: 'return_days',
                          width: 110,
                          align: 'center',
                          render: (v: number | null) =>
                            v != null ? (
                              <Tag color="blue">{v} dias</Tag>
                            ) : (
                              <Tag color="default">—</Tag>
                            ),
                        },
                        {
                          title: 'Último Atendimento',
                          dataIndex: 'last_service_date',
                          width: 150,
                          align: 'center',
                          render: (v: string | null) => {
                            if (!v) return <Tag color="default">Sem atendimento</Tag>;
                            return new Date(v + 'T00:00:00').toLocaleDateString('pt-BR');
                          },
                        },
                        {
                          title: 'Próximo Vencimento',
                          dataIndex: 'next_due_date',
                          width: 160,
                          align: 'center',
                          render: (v: string | null, row: any) => {
                            if (!v) return <Tag color="default">Não calculado</Tag>;
                            const dt = new Date(v + 'T00:00:00');
                            let color: any = 'green';
                            if (row.status === 'vence_hoje') color = 'orange';
                            else if (row.status === 'atrasado') color = 'red';
                            return (
                              <Tag color={color}>
                                {dt.toLocaleDateString('pt-BR')}
                              </Tag>
                            );
                          },
                        },
                        {
                          title: 'Situação',
                          dataIndex: 'status',
                          width: 150,
                          align: 'center',
                          render: (_: any, row: any) => {
                            if (row.status === 'em_dia') return <Tag color="green">Em dia</Tag>;
                            if (row.status === 'vence_hoje') return <Tag color="orange">Vence hoje</Tag>;
                            if (row.status === 'atrasado') {
                              const d = Number(row.overdue_days ?? 0) || 0;
                              return (
                                <Tag color="red">
                                  Atrasado • {d} dia{d === 1 ? '' : 's'}
                                </Tag>
                              );
                            }
                            return <Tag color="default">Sem atendimento</Tag>;
                          },
                        },
                        {
                          title: 'Total Itens',
                          dataIndex: 'total_items',
                          width: 100,
                          align: 'right',
                          render: (v: number) => <strong>{v}</strong>,
                        },
                        {
                          title: 'Pendentes',
                          dataIndex: 'pending_returns',
                          width: 110,
                          align: 'right',
                          fixed: screens.xs || screens.sm ? 'right' : undefined,
                          render: (v: number) => (
                            <Tag color={v > 0 ? 'warning' : 'default'}>{v}</Tag>
                          ),
                        },
                      ]}
                      pagination={false}
                    />
                  </Card>
                </Space>
              ) : tab === 'FUTURE_CONTACTS' ? (
                <Card size="small" style={{ background: '#fafafa' }} bordered={false}>
                  <AntTable
                    size={screens.xs || screens.sm ? 'middle' : 'small'}
                    loading={loadingTab}
                    dataSource={(futureContactsReport?.rows ?? []).map((r, i) => ({
                      ...r,
                      id: `${r.source}-${r.client_id}-${r.procedure_id}-${r.return_due_date}-${i}`,
                    }))}
                    rowKey="id"
                    scroll={{ x: 1040 }}
                    columns={[
                      {
                        title: 'Cliente',
                        dataIndex: 'client_name',
                        width: 200,
                        fixed: screens.xs || screens.sm ? 'left' : undefined,
                        render: (v: string, row: any) =>
                          screens.md ? (
                            <Space direction="vertical" size={0}>
                              <strong>{v}</strong>
                              {row.client_cpf_cnpj && (
                                <span style={{ fontSize: 11, color: '#8c8c8c' }}>
                                  {row.client_cpf_cnpj}
                                </span>
                              )}
                            </Space>
                          ) : (
                            <strong>{v}</strong>
                          ),
                      },
                      {
                        title: 'Contato',
                        width: 180,
                        render: (_: any, row: any) => {
                          const phone =
                            row.cell_phone || row.home_phone || row.office_phone;
                          return phone ? (
                            screens.md ? (
                              <Space direction="vertical" size={0}>
                                <span>{phone}</span>
                                {row.email && (
                                  <span style={{ fontSize: 11, color: '#8c8c8c' }}>
                                    {row.email}
                                  </span>
                                )}
                              </Space>
                            ) : (
                              <span>{phone}</span>
                            )
                          ) : row.email ? (
                            <span>{row.email}</span>
                          ) : (
                            <Tag color="default">Sem contato</Tag>
                          );
                        },
                      },
                      {
                        title: 'Procedimento',
                        dataIndex: 'procedure_description',
                        ellipsis: true,
                        minWidth: 180,
                      },
                      {
                        title: 'Profissional',
                        dataIndex: 'professional_name',
                        width: 180,
                        ellipsis: true,
                      },
                      {
                        title: 'Vencimento Retorno',
                        dataIndex: 'return_due_date',
                        width: 160,
                        align: 'center',
                        render: (v: string) => {
                          if (!v) return <Tag color="default">—</Tag>;
                          const dt = new Date(v + 'T00:00:00');
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const diff = Math.floor(
                            (dt.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
                          );
                          let color = 'green';
                          if (diff < 0) color = 'red';
                          else if (diff <= 7) color = 'orange';
                          const formatted = dt.toLocaleDateString('pt-BR');
                          return <Tag color={color as any}>{formatted}</Tag>;
                        },
                      },
                      {
                        title: 'Origem',
                        dataIndex: 'source',
                        width: 130,
                        align: 'center',
                        fixed: screens.xs || screens.sm ? 'right' : undefined,
                        render: (v: 'clinical' | 'dental') =>
                          v === 'dental' ? (
                            <Tag color="geekblue">Odontológico</Tag>
                          ) : (
                            <Tag color="blue">Clínico</Tag>
                          ),
                      },
                    ]}
                    pagination={false}
                  />
                </Card>
              ) : tab === 'PERFORMANCE' || tab === 'SUMMARY' ? (
                <Row gutter={[16, 16]}>
                  <Col xs={24} lg={14}>
                    <Card
                      title="Desempenho por Atendente"
                      size="small"
                      bordered={false}
                      style={{ background: '#fafafa' }}
                    >
                      <Bar
                        data={performanceData}
                        height={260}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          indexAxis: 'y',
                          plugins: { legend: { position: 'bottom' as const } },
                          scales: { x: { beginAtZero: true } },
                        }}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} lg={10}>
                    <Card
                      title="Top Especialidades"
                      size="small"
                      bordered={false}
                      style={{ background: '#fafafa' }}
                    >
                      <Doughnut
                        data={specialtiesData}
                        height={260}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          cutout: '60%',
                          plugins: { legend: { position: 'bottom' as const } },
                        }}
                      />
                    </Card>
                  </Col>
                </Row>
              ) : (
                <Card
                  size="small"
                  style={{ background: '#fafafa' }}
                  bordered={false}
                  title={
                    rowsForTab.titleExtra ? (
                      <Space size={6}>
                        <FileTextOutlined />
                        <span style={{ fontSize: 13, color: '#262626' }}>
                          {rowsForTab.titleExtra}
                        </span>
                      </Space>
                    ) : (
                      <Space size={6}>
                        <FileTextOutlined />
                        Detalhamento do relatório selecionado
                      </Space>
                    )
                  }
                >
                  <AntTable
                    size={screens.xs || screens.sm ? 'middle' : 'small'}
                    loading={loading}
                    scroll={{ x: 780 }}
                    dataSource={rowsForTab.data}
                    rowKey="id"
                    pagination={false}
                    columns={[
                      { title: 'Item / Métrica', dataIndex: 'item', minWidth: 200 },
                      {
                        title: 'Período Atual',
                        dataIndex: 'qtd_current',
                        width: 140,
                        render: (v: number | string) => <strong>{v}</strong>,
                      },
                      {
                        title: 'Período Anterior',
                        dataIndex: 'qtd_previous',
                        width: 140,
                        render: (v: number | string) => (
                          <span style={{ color: '#888' }}>{v}</span>
                        ),
                      },
                      {
                        title: 'Variação',
                        dataIndex: 'delta',
                        width: 130,
                        render: (v: number) => renderVariation(Number(v) || 0),
                      },
                      {
                        title: 'Participação / %',
                        dataIndex: 'perc',
                        width: 200,
                        render: (v: number) => (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                            }}
                          >
                            <div
                              style={{
                                flex: 1,
                                background: '#f0f0f0',
                                height: 8,
                                borderRadius: 4,
                                overflow: 'hidden',
                              }}
                            >
                              <div
                                style={{
                                  width: `${Math.max(0, Math.min(100, Number(v) || 0))}%`,
                                  background:
                                    (Number(v) || 0) > 50
                                      ? '#52c41a'
                                      : (Number(v) || 0) > 25
                                      ? '#1677ff'
                                      : '#faad14',
                                  height: '100%',
                                }}
                              />
                            </div>
                            <span
                              style={{
                                fontSize: 12,
                                minWidth: 36,
                                textAlign: 'right',
                              }}
                            >
                              {Number(v || 0).toFixed(0)}%
                            </span>
                          </div>
                        ),
                      },
                      {
                        title: 'Observação',
                        dataIndex: 'extra',
                        minWidth: 180,
                        render: (v: string | undefined) =>
                          v ? (
                            <Tag color="purple" style={{ marginInlineEnd: 0 }}>
                              {v}
                            </Tag>
                          ) : (
                            <span style={{ color: '#bbb' }}>—</span>
                          ),
                      },
                    ]}
                  />
                </Card>
              )}
            </Card>
          </Col>

          {!actionsPanelCollapsed && (
            <Col xs={24} xl={7}>
              <Space direction="vertical" style={{ width: '100%' }} size={16}>
                <Card
                  bordered={false}
                  size="small"
                  title={
                    <Space>
                      <DownloadOutlined />
                      Exportar / Ações
                    </Space>
                  }
                >
                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                  {permissionFind([PermissionActions.crcReportsExport]) && (
                    <>
                      <Tooltip title="Gerar relatório em PDF">
                        <AButton
                          block
                          size="large"
                          icon={<FilePdfOutlined />}
                          style={{
                            background: '#ff4d4f',
                            borderColor: '#ff4d4f',
                            color: '#fff',
                            fontWeight: 500,
                          }}
                          onClick={() => handleExport('pdf')}
                        >
                          Exportar PDF
                        </AButton>
                      </Tooltip>
                      <Tooltip title="Gerar planilha Excel">
                        <AButton
                          block
                          size="large"
                          icon={<FileExcelOutlined />}
                          style={{
                            background: '#52c41a',
                            borderColor: '#52c41a',
                            color: '#fff',
                            fontWeight: 500,
                          }}
                          onClick={() => handleExport('xlsx')}
                        >
                          Exportar Excel
                        </AButton>
                      </Tooltip>
                    </>
                  )}
                  <Tooltip title="Imprimir relatório atual">
                    <AButton block size="large" icon={<PrinterOutlined />} onClick={handlePrint}>
                      Imprimir
                    </AButton>
                  </Tooltip>
                  <Tooltip title="Enviar relatório por e-mail">
                    <AButton
                      block
                      size="large"
                      type="primary"
                      icon={<MailOutlined />}
                      onClick={handleEmail}
                    >
                      Enviar por E-mail
                    </AButton>
                  </Tooltip>
                </Space>
              </Card>

              <Card
                bordered={false}
                size="small"
                title={
                  <Space>
                    <SaveOutlined />
                    Relatórios Salvos
                  </Space>
                }
                extra={
                  <AButton
                    size="small"
                    type="text"
                    icon={<DownloadOutlined />}
                    onClick={handleDownloadSaved}
                    disabled={checkedSaved.length === 0}
                  >
                    Baixar ({checkedSaved.length})
                  </AButton>
                }
              >
                <Checkbox
                  indeterminate={
                    checkedSaved.length > 0 && checkedSaved.length < savedReports.length
                  }
                  checked={
                    savedReports.length > 0 && checkedSaved.length === savedReports.length
                  }
                  onChange={(e) => {
                    if (e.target.checked) {
                      setCheckedSaved(savedReports.map((s) => s.id));
                    } else {
                      setCheckedSaved([]);
                    }
                  }}
                  style={{ marginBottom: 8, fontWeight: 600 }}
                >
                  Selecionar todos
                </Checkbox>
                <Divider style={{ margin: '6px 0' }} />
                <List
                  size="small"
                  dataSource={savedReports}
                  renderItem={(item) => (
                    <List.Item
                      style={{
                        padding: '6px 0',
                        borderBottom: '1px dashed #f0f0f0',
                      }}
                      actions={[
                        <Tag color="blue" key="type" style={{ fontSize: 11 }}>
                          {item.type}
                        </Tag>,
                      ]}
                    >
                      <List.Item.Meta
                        avatar={
                          <Checkbox
                            checked={checkedSaved.includes(item.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setCheckedSaved((p) => [...p, item.id]);
                              } else {
                                setCheckedSaved((p) => p.filter((x) => x !== item.id));
                              }
                            }}
                          />
                        }
                        title={
                          <span style={{ fontSize: 13, fontWeight: 500 }}>
                            {item.name}
                          </span>
                        }
                        description={
                          <span style={{ fontSize: 11, color: '#999' }}>
                            {item.date} · {item.size} · por {item.author}
                          </span>
                        }
                      />
                    </List.Item>
                  )}
                />
              </Card>
            </Space>
            </Col>
          )}
        </Row>
      </PagePanel>
    </PageLayout>
  );
};

export default CrcReports;
